import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, InfoIcon, X } from "lucide-react";
import { toast } from "react-toastify";
import { networkService } from "~/services";
import { handleError } from "~/shared/utils/errors.util";
import { formatDate } from "~/shared/utils/helper.util";
import useAuthStore from "~/store/auth.store";
import useSidebarStore from "~/store/sidebar.store";
import { UserRole } from "~/shared/config";
import { GET_LICENSES_BY_ORG_ID } from "~/shared/constants/api";
import { PaymentsIcon } from "~/shared/icon/payments-icon";
import linkedProbe from "~/assets/images/linked-probe.png";
import { LicenseThinIcon } from "~/shared/icon/license-thin-icon";
import cancelLicenseLandScape from "../../../../public/cancel-license-landscape.svg";
import cancelLicenseLandScape1 from "../../../../public/cancel-license-landscape-1.svg"
import gradient from "../../../../public/Gradient.svg";

// import heartFocusImg from "../../assets/images/heartfocus-modal.png";

interface LicenseDetails {
    id: string;
    isActive: boolean;
    subscription: {
        id: string;
        organization: {
            id: string;
            name: string;
        };
        plan: {
            service: {
                id: string;
                name: string;
            };
            id: string;
            name: string;
            pricingModel: {
                type: string;
                billingCycle: string;
                unitPrice: number;
                unit: string;
                interval: string;
                intervalCount: number;
            };
        };
        metaData: {
            trialEndDate?: string;
            stripeSubscriptionId?: string;
        } | null;
        cancelledAt: string | null;
        autoRenewActive: boolean;
        periodEndDate: string | null;
        paymentFailedAt: string | null;
        isTrial: boolean;
        paymentFailReasonCode: string | null;
    };
    device?: {
        id: string;
        externalDeviceId: string;
        nickName: string;
        modelId: string;
        isActive: boolean;
        model?: {
            modelName: string;
            manufacturer?: {
                name: string;
            };
        };
    } | null;
    user?: any;
    project?: any;
    cancelledAt: string | null;
    autoRenewActive: boolean;
    paymentFailedAt: string | null;
    isTrial: boolean;
    paymentFailReasonCode: string | null;
    metaData: any; // can be null
    createdAt: string;
    updatedAt: string;
    expiryDate: string;
}

// Helper to format date as 'May 1st 2026'
export function formatDateWithOrdinal(dateString: string) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    // Ordinal suffix
    const j = day % 10, k = day % 100;
    let suffix = 'th';
    if (j === 1 && k !== 11) suffix = 'st';
    else if (j === 2 && k !== 12) suffix = 'nd';
    else if (j === 3 && k !== 13) suffix = 'rd';
    return `${month} ${day}${suffix} ${year}`;
}

// Function to determine model from serial number
const getModelFromSerialNumber = (serialNumber: string) => {
    if (!serialNumber) return "Probe unknown";
    const prefix = serialNumber.substring(0, 2).toUpperCase();
    if (prefix === "BT") {
        return "iQ+";
    } else if (prefix === "KB") {
        return "iQ3";
    } else {
        return "Probe unknown";
    }
};

// Function to determine if serial number should be displayed
// const shouldDisplaySerialNumber = (serialNumber: string) => {
//     if (!serialNumber) return false;
//     const prefix = serialNumber.substring(0, 2).toUpperCase();
//     return prefix === "BT" || prefix === "KB";
// };

const LicenseDetailsPage = () => {
    const { licenseId } = useParams();
    const navigate = useNavigate();
    const [license, setLicense] = useState<LicenseDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [collapsed] = useSidebarStore((state) => [state.collapsed]);
    const { user } = useAuthStore((state) => ({
        user: state.user,
    }));
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);

    // Refetch license details
    const fetchLicenseDetails = async () => {
        setLoading(true);
        try {
            const response = await networkService.get<any>(
                `${GET_LICENSES_BY_ORG_ID}/${user?.organizationId}/${licenseId}`
            );
            setLicense(response.data);
        } catch (error: any) {
            handleError({ error: error, message: error.message, level: "error" });
            toast.error("Failed to fetch license details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (licenseId && user?.organizationId) {
            fetchLicenseDetails();
        }
    }, [licenseId, user?.organizationId]);

    const handleCancelLicense = async () => {
        try {
            const response = await networkService.post<any>(`/licenses/cancel-at-period-end/${licenseId}`, {});
            console.log('Cancel subscription response:', response);
            toast.success("Subscription cancellation requested successfully");
            setIsCancelModalOpen(false);
            // Refetch license details to update UI
            fetchLicenseDetails();
        } catch (error: any) {
            handleError({ error: error, message: error.message, level: "error" });
            toast.error("Failed to request subscription cancellation");
        }
    };

    const handleReactivateLicense = async () => {
        try {
            // 1. Get all licenses for the subscription
            const subId = license?.subscription?.id;
            if (!subId) throw new Error('Subscription ID not found');
            const licensesRes = await networkService.get<any>(`/licenses/subscription/${subId}`);
            const licensesArr = Array.isArray(licensesRes.data) ? licensesRes.data : licensesRes.data.results || [];
            // 2. If only one license and not a trial, reactivate the subscription too
            if (licensesArr.length === 1 && licensesArr[0].isTrial === false) {
                await networkService.post(`/subscriptions/uncancel-at-period-end/${subId}`, {});
            } else {
                // Multi-license: reactivate just the license
                await networkService.post(`/licenses/uncancel-at-period-end/${licenseId}`, {});
            }
            toast.success("License reactivation requested successfully");
            setIsReactivateModalOpen(false);
            fetchLicenseDetails();
        } catch (error: any) {
            handleError({ error: error, message: error.message, level: "error" });
            toast.error("Failed to request license reactivation");
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#807CD6]"></div>
                <p className="mt-4 text-gray-600">Loading license details...</p>
            </div>
        );
    }

    if (!license) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] py-12">
                <p className="text-gray-600">License not found</p>
            </div>
        );
    }

    const isTrial = license?.isTrial === true;
    const trialEndDate = license.subscription.metaData?.trialEndDate
        ? formatDate(license.subscription.metaData.trialEndDate)
        : null;
    const creationDate = license.createdAt ? formatDate(license.createdAt) : "-";
    const expiryDate = license.expiryDate ? formatDateWithOrdinal(license.expiryDate) : "-";
    const plan = license.subscription.plan;
    const price = plan.pricingModel?.unitPrice;
    const billingCycle = plan.pricingModel?.billingCycle;
    const intervalCount = plan.pricingModel?.intervalCount;
    const deviceId = license.device?.id;

    // Calculate the day after expiryDate for the info banner
    let expiryDateObj = license.expiryDate ? new Date(license.expiryDate) : null;
    let dayAfterExpiry = expiryDateObj ? new Date(expiryDateObj.getTime() + 24 * 60 * 60 * 1000) : null;
    const formattedDayAfterExpiry = dayAfterExpiry ? formatDateWithOrdinal(dayAfterExpiry.toISOString()) : "-";

    const showNonRenewed = license.isTrial === false && license.autoRenewActive === false && license.isActive === true;

    const handleUnattachProbe = async () => {
        try {
            if (!license?.device?.id) {
                toast.error("No probe attached to unattach");
                return;
            }

            await networkService.put(`/licenses/unattach/device/${licenseId}`, { deviceId: license.device.id });
            toast.success("Probe unattached successfully");
            // Refetch license details to update UI
            fetchLicenseDetails();
        }
        catch (error: any) {
            handleError({ error: error, message: error.message, level: "error" });
            toast.error("Failed to unattach probe");
        }
    }

    return (
        <div>
            <div className={`md:hidden flex flex-col flex-grow mb-2 font-space-grotesk ${collapsed
                ? `${user?.role === UserRole.Admin ? "ml-20" : "md:ml-20"}`
                : `${user?.role === UserRole.Admin ? "ml-72" : "md:ml-72"}`
                } transition-all duration-300`}>
                <div className="relative block md:hidden">
                    <div className="flex gap-4 items-center px-10 py-5 lg:justify-start shadow-none lg:shadow-none">
                        <button
                            onClick={() => navigate(-1)}
                            className="border-2 border-black-600 p-2 rounded-full lg:hidden z-50"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <h1 className="text-[20px] lg:text-3xl font-bold font-space-grotesk text-center md:text-left ml-[3rem] md:ml-1 lg:ml-0 z-50">
                            License Details
                        </h1>
                    </div>
                    <div className="border-t lg:border-t-0 border-[var(--Stroke,#EFEFF4)] shadow-md lg:shadow-none absolute bottom-[-0.25rem] h-28 left-[-55px] right-0 bg-white-a700"></div>
                </div>
            </div>
            <div>
                <div
                    className={`flex flex-col flex-grow px-10 py-5 mb-2 font-space-grotesk ${collapsed
                        ? `${user?.role === UserRole.Admin ? "ml-20" : "md:ml-20"}`
                        : `${user?.role === UserRole.Admin ? "ml-72" : "md:ml-72"}`
                        } transition-all duration-300 pb-24`}
                >
                    {/* Breadcrumb */}
                    <div className="text-sm text-gray-500 mb-2 hidden md:block">
                        <span
                            className="cursor-pointer hover:underline"
                            onClick={() => navigate("/licenses")}
                        >
                            Licenses
                        </span>
                        <span className="mx-2">&gt;</span>
                        <span>License Details</span>
                    </div>

                    <h1 className="text-3xl font-bold mb-4 hidden md:block">License Details</h1>

                    {/* Info Banner for cancelled auto-renewal (non-trial) */}
                    {license.isTrial === false && license.autoRenewActive === false && (
                        <div className="bg-[#FDEDF2] border border-[#F5C6C6] text-[#C94A6A] rounded-md px-6 py-4 mb-6">
                            <div className="font-medium flex items-center gap-2">
                                <span className="text-lg">&#9888;</span>
                                The auto-renewal of your license has been stopped.
                            </div>
                            <div className="text-[#C94A6A] mt-1">
                                Your access to HeartFocus will be automatically revoked on {expiryDate}.
                            </div>
                        </div>
                    )}
                    {/* Trial Banners */}
                    {isTrial && license.device === null && (
                        <div className="bg-[#FDF5EF] border border-[#F3CCB5] text-[#CC462A] rounded-md px-6 py-4 mb-6 flex items-start">
                            <InfoIcon size={18} className="inline-block mr-3" />
                            <div>
                                <strong>No probe is linked to your license</strong>
                                <div className="text-[#CC462A] mt-1">
                                    You won't be able to use HeartFocus without adding your probe serial number.
                                </div>
                            </div>
                        </div>
                    )}
                    {isTrial && license.device !== null && (
                        <div className="bg-[#EDEDFA] border border-[#C4C6EE] text-[#807CD6] rounded-md px-6 py-4 mb-6 flex items-start">
                            <InfoIcon size={18} className="inline-block mr-3 w-20 mt-[2px]" />
                            <div>
                                <strong>Your Free Trial is active until {expiryDate}.</strong>
                                <div className="text-[#5B4DB4] mt-1">
                                    You will be automatically charged on {trialEndDate}. We will send you a reminder before your trial ends.
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Linked Probe */}
                        <div className="bg-white border rounded-lg p-6 flex flex-col min-h-[180px]">
                            <div className="flex items-center justify-between mb-4">
                                <span className="font-semibold text-lg flex items-center gap-2">
                                    <span role="img" aria-label="probe">
                                        <img src={linkedProbe} className="w-6 h-6" />
                                    </span> Linked Probe
                                </span>
                                <div className="gap-2 items-center hidden md:flex">
                                    {license.device && (
                                        <button
                                            className="border-2 px-3 py-1.5 hover:bg-gray-100 text-sm rounded-full"
                                            onClick={handleUnattachProbe}
                                        >Remove</button>
                                    )}
                                    <div className="flex w-20 justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                        <button
                                            type="submit"
                                            className="w-full text-sm bg-white-a700 rounded-full px-3 py-1 hover:bg-light-angular-gradient"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (license?.device?.id) {
                                                    // If probe is attached, go to edit mode
                                                    navigate(`/probes/update-probe-details/${user.organizationId}/${license.device.id}`);
                                                } else {
                                                    // If no probe attached, go to attach new probe
                                                    navigate(`/probes/update-probe-details/${user.organizationId}/new?licenseId=${license?.id}`);
                                                }
                                            }}>
                                            Edit
                                        </button>
                                    </div>
                                </div>
                                {/* <div className="flex w-20 justify-center items-center bg-angular-gradient rounded-full p-0.5">
                            <button
                                type="submit"
                                className="w-full text-sm bg-white-a700 rounded-full px-3 py-1 hover:bg-light-angular-gradient"
                                onClick={() => navigate(`/probes/update-probe-details/${user?.organizationId}/${license.device?.id}`)}
                            >
                                Edit
                            </button>
                        </div> */}
                            </div>
                            {license.device ? (
                                <div>
                                    <div className="mb-1 text-sm flex flex-col">
                                        <span className="font-medium">Model</span>
                                        <span className="text-sm mt-[1px]">
                                            {getModelFromSerialNumber(license.device.externalDeviceId)}
                                        </span>
                                    </div>
                                    <div className="mb-1 mt-3 text-sm flex flex-col">
                                        <span className="font-medium">Serial Number</span>
                                        <span className="text-sm mt-[1px]">
                                            {license.device.externalDeviceId}
                                        </span>
                                    </div>
                                    <div className="mb-1 mt-3 text-sm flex flex-col">
                                        <span className="font-medium">Name</span>
                                        <span className="text-sm mt-[1px]">{license.device.nickName}</span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-1 text-sm flex flex-col"><span className="font-medium">Model</span> <span className="text-sm mt-[1px]">-</span></div>
                                    <div className="mb-1 mt-3 text-sm flex flex-col"><span className="font-medium">Serial Number</span> <span className="text-sm mt-[1px]">-</span></div>
                                    <div className="mb-1 mt-3 text-sm flex flex-col"><span className="font-medium">Name</span> <span className="text-sm mt-[1px]">-</span></div>
                                </div>
                            )}
                            <div className="flex gap-4 items-center md:hidden w-full mt-4 text-base font-semibold">
                                {license.device && (
                                    <button
                                        className="border-2 px-3 py-[10px] w-full hover:bg-gray-100 text-sm rounded-full"
                                        onClick={handleUnattachProbe}
                                    >Remove</button>
                                )}
                                <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                    <button
                                        type="submit"
                                        className="w-full bg-white-a700 rounded-full px-3 py-2 hover:bg-light-angular-gradient"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (license?.device?.id) {
                                                // If probe is attached, go to edit mode
                                                navigate(`/probes/update-probe-details/${user.organizationId}/${license.device.id}`);
                                            } else {
                                                // If no probe attached, go to attach new probe
                                                navigate(`/probes/update-probe-details/${user.organizationId}/new?licenseId=${license?.id}`);
                                            }
                                        }}>
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* License Attributes */}
                        <div className="bg-white border rounded-lg p-6 flex flex-col min-h-[180px]">
                            <span className="font-semibold text-lg flex items-center gap-2 mb-4">
                                <span role="img" aria-label="attributes">
                                    <LicenseThinIcon width="18" height="18" />
                                </span> License Attributes
                            </span>
                            <div className="mb-1 text-sm"><span className="font-medium flex flex-col">State</span> <span className="text-sm mt-[1px]">{license.isActive ? "Active" : "Inactive"}</span></div>
                            <div className="mb-1 mt-3 text-sm"><span className="font-medium flex flex-col">ID</span> <span className="text-sm mt-[1px]">{license.id}</span></div>
                            <div className="mb-1 mt-3 text-sm"><span className="font-medium flex flex-col">Creation Date</span> <span className="text-sm mt-[1px]">{creationDate}</span></div>
                        </div>
                    </div>

                    {/* Billing & Payment */}
                    <div className="bg-white border rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6 w-full">
                        <div className="w-full">
                            <div className="w-full font-semibold text-lg flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-3">
                                    <span role="img" aria-label="billing">
                                        <PaymentsIcon width="18" height="18" />
                                    </span>
                                    <span>
                                        Billing & payment
                                    </span>
                                </div>
                                <div className="flex items-center md:justify-end">
                                    {/* Show Reactivate button if license is active but auto-renew is off */}
                                    {license.isActive && license.autoRenewActive === false ? (
                                        <div className="md:flex hidden w-full justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setIsReactivateModalOpen(true)}
                                                className="w-full font-semibold bg-white-a700 rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                            >
                                                Reactivate license
                                            </button>
                                        </div>
                                    ) : null}
                                    {/* Show Cancel Subscription button if license is active and auto-renew is on */}
                                    {license.isActive && license.autoRenewActive !== false && (
                                        <div className="md:flex hidden w-full justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                            <button
                                                type="submit"
                                                onClick={() => setIsCancelModalOpen(true)}
                                                className="w-full font-semibold bg-white-a700 text-sm rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                            >
                                                Cancel subscription
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col md:flex-row md:gap-40 gap-5">
                                <div>
                                    <div className="text-sm mt-1">${price} / {intervalCount === 1 ? "year" : "year"}</div>
                                    <div className="text-sm text-gray-500 my-1">Next payment: {showNonRenewed ? "-" : expiryDate}</div>
                                    <button className="text-sm underline font-medium" onClick={() => navigate('/billing')}>See payment history</button>
                                </div>
                                <div className="">
                                    <div className="text-sm text-gray-500">Card ending by **** 4242</div>
                                    <button className="text-sm underline font-medium" onClick={() => navigate('/billing')}>Manage payment method</button>
                                </div>
                            </div>
                        </div>
                        {license.isActive && license.autoRenewActive === false ? (
                            <div className="flex md:hidden w-full justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                <button
                                    type="button"
                                    onClick={() => setIsReactivateModalOpen(true)}
                                    className="w-full font-semibold bg-white-a700 rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                >
                                    Reactivate license
                                </button>
                            </div>
                        ) : null}
                        {license.isActive && license.autoRenewActive !== false && (
                            <div className="flex md:hidden w-full justify-center items-center bg-angular-gradient rounded-full p-0.5">
                                <button
                                    type="submit"
                                    onClick={() => setIsCancelModalOpen(true)}
                                    className="w-full bg-white-a700 text-base font-semibold rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                >
                                    Cancel subscription
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Cancel Subscription Modal */}
                    {isCancelModalOpen && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black-900 bg-opacity-50">
                            <div className="bg-white-a700 shadow-lg flex flex-col relative mb-12 max-w-4xl w-full rounded-3xl mx-5 md:mx-0">
                                <div className="font-space-grotesk">
                                    {/* Header with close button */}
                                    <div className="flex justify-between items-center px-7 pb-4 pt-6">
                                        <span className="font-medium">Subscription Cancellation</span>
                                        <button
                                            onClick={() => setIsCancelModalOpen(false)}
                                            className="text-2xl"
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {/* Horizontal separator that spans full width */}
                                    <div className="w-full h-px bg-gray-200"></div>
                                </div>
                                <div className="flex flex-col md:flex-row">
                                    {/* left container information */}
                                    <div className="px-7 pt-5 w-full md:w-3/5">
                                        <h2 className="text-3xl font-bold mb-5">Sure you want to cancel?</h2>
                                        <div className="bg-[#EDEDFA] border border-[#C4C6EE] text-[#807CD6] rounded-md px-4 py-3 mb-6 text-base flex gap-2">
                                            <div className="pt-1">
                                                <InfoIcon width="18" height="18" />
                                            </div>
                                            <div>
                                                <div className="font-medium">You will not be able to use HeartFocus from {formattedDayAfterExpiry}.</div>
                                                <div className="text-[#807CD6]">Your subscription will still be active until {expiryDate}.</div>
                                            </div>
                                        </div>
                                        <div className="text-base mb-6">
                                            You will lose access to the unique features of HeartFocus to assist you in the acquisition of cardiac ultrasound images:
                                        </div>
                                        <ul className="mb-8 space-y-3">
                                            <li className="flex items-start gap-2 text-base text-gray-700">
                                                <span className="text-[#807CD6] font-bold">✕</span>
                                                <span className="ml-3">Live-guidance that gives you cues on how to move the probe to get diagnostic-quality images</span>
                                            </li>
                                            <li className="flex items-start gap-2 text-base text-gray-700">
                                                <span className="text-[#807CD6] font-bold">✕</span>
                                                <span className="ml-3">Automatic recording of a clip when the quality is predicted to be of diagnostic quality</span>
                                            </li>
                                            <li className="flex items-start gap-2 text-base text-gray-700">
                                                <span className="text-[#807CD6] font-bold">✕</span>
                                                <span className="ml-3">Quality assessment of the recorded clip</span>
                                            </li>
                                        </ul>
                                        <div className="flex flex-col gap-4 mb-7">
                                            <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5 mt-5">
                                                <button
                                                    type="submit"
                                                    onClick={handleCancelLicense}
                                                    className="w-full font-semibold bg-white-a700 rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                                >
                                                    Cancel subscription
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right container image */}
                                    <div className="flex-1 relative w-full md:w-2/5 hidden md:block">
                                        {/* Gradient background using your SVG */}
                                        <div
                                            className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-br-3xl"
                                            style={{
                                                backgroundImage: `url(${gradient})`
                                            }}
                                        ></div>

                                        {/* Overlapping images container */}
                                        <div className="absolute inset-0 flex items-center justify-center p-8">
                                            <div className="relative w-full max-w-sm">
                                                {/* First image - positioned behind and slightly offset */}
                                                <div className="absolute -top-10 left-12 w-60 h-44 rounded-lg transform z-20">
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <img src={cancelLicenseLandScape1} alt="cancel-license-landscape" />
                                                    </div>
                                                </div>

                                                {/* Second image - positioned in front */}
                                                <div className="absolute -top-48 left-2 w-60 h-44 rounded-lg transform z-10">
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <img src={cancelLicenseLandScape} alt="cancel-license-landscape" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reactivate License Modal */}
                    {isReactivateModalOpen && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black-900 bg-opacity-50">
                            <div className="bg-white-a700 shadow-lg flex flex-col relative mb-12 max-w-4xl w-full max-h-fit rounded-3xl mx-5 md:mx-0">
                                <div className="font-space-grotesk">
                                    {/* Header with close button */}
                                    <div className="flex justify-between items-center px-7 pb-4 pt-6">
                                        <span className="font-medium">Subscription Cancellation</span>
                                        <button
                                            onClick={() => setIsCancelModalOpen(false)}
                                            className="text-2xl"
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {/* Horizontal separator that spans full width */}
                                    <div className="w-full h-px bg-gray-200"></div>
                                </div>
                                {/* Horizontal separator that spans full width */}

                                <div className="flex flex-col md:flex-row">
                                    {/* left information container */}
                                    <div className="px-7 py-5 w-full md:w-3/5">
                                        <h2 className="text-3xl font-bold mb-5">Want to rejoin?</h2>
                                        <div className="mb-4 text-base">Your license is active until {expiryDate}.</div>
                                        <div className="mb-8 text-base">By reactivating, auto-renewal will resume on that date, and you'll continue to have uninterrupted access to HeartFocus.</div>
                                        <div className="flex flex-col gap-4">
                                            <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5 mt-5">
                                                <button
                                                    type="button"
                                                    onClick={handleReactivateLicense}
                                                    className="w-full font-semibold bg-white-a700 rounded-full px-4 py-2 hover:bg-light-angular-gradient"
                                                >
                                                    Reactivate License
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIsReactivateModalOpen(false)}
                                                className="w-full py-2 rounded-full font-medium border-[2px] border-gray-200"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                    {/* right images container */}
                                    <div className="flex-1 relative w-full md:w-2/5 hidden md:block">
                                        {/* Gradient background using your SVG */}
                                        <div
                                            className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-br-3xl"
                                            style={{
                                                backgroundImage: `url(${gradient})`
                                            }}
                                        ></div>

                                        {/* Overlapping images container */}
                                        <div className="absolute inset-0 flex items-center justify-center p-8">
                                            <div className="relative w-full max-w-sm">
                                                {/* First image - positioned behind and slightly offset */}
                                                <div className="absolute -top-16 left-20 w-52 h-56 rounded-lg transform z-20">
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <img src={cancelLicenseLandScape1} alt="cancel-license-landscape" />
                                                    </div>
                                                </div>

                                                {/* Second image - positioned in front */}
                                                <div className="absolute -top-48 left-2 w-52 h-56 rounded-lg transform z-10">
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <img src={cancelLicenseLandScape} alt="cancel-license-landscape" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LicenseDetailsPage; 